import { widgetApi } from "../services/api";

/** Deferred payout binding states (sender-side lifecycle). */
export type DeferredBindingState =
  | "payment_bound"
  | "awaiting_claim"
  | "payout_bound"
  | "executable"
  | "ofac_rejected"
  | "expired"
  | "cancelled";

export interface RecipientHint {
  firstName: string;
  lastName: string;
  fullName?: string;
}

export interface SendCommitment {
  sendAmount: number;
  totalDebit: number;
  sendCurrency: string;
  sourceCountry: string;
  destinationCountry: string;
  destinationCurrency: string;
  fundingMethod: string;
  fundingPaymentMethodId?: string;
}

export interface OriginationSummary {
  originationId: string;
  bindingState: DeferredBindingState;
  sendAmount: number;
  sendCurrency: string;
  destinationCurrency: string;
  recipientHint: RecipientHint;
  payoutClaimUrl?: string | null;
  operationId?: string | null;
  /** Operation lifecycle status when bound (e.g. completed, processing). */
  operationStatus?: string | null;
  /** Canonical funding method from the send commitment / operation. */
  fundingMethod?: string | null;
  /** Push-funding sub-status when the operation exists (e.g. awaiting_funding). */
  fundingStatus?: string | null;
  createdAt: string;
}

export interface OriginationOperationDetail {
  operationId: string;
  status: string;
  sendAmount: number;
  receiveAmount: number;
  exchangeRate: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  totalCost: number;
  sourceCurrency: string;
  destinationCurrency: string;
  fundingMethod?: string;
  fundingStatus?: string;
  recipient?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
  /** Canonical corridor references (payout code, tracking ref). */
  references?: Array<{
    type: string;
    value: string;
    label?: Record<string, string>;
    presentation: string;
    issuedAt?: string;
  }>;
  /** Canonical documents the corridor can issue live (PDF receipts). */
  documents?: Array<{
    kind: string;
    mediaType: string;
    freshness: "LIVE" | "IMMUTABLE";
    availability: "AVAILABLE" | "PENDING" | "UNAVAILABLE";
    locales?: string[];
  }>;
  createdAt: string;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
    unavailableReason?: string;
    requestedAt?: string;
  };
}

export interface OriginationDetail {
  originationId: string;
  bindingState: DeferredBindingState;
  sendCommitment: SendCommitment;
  recipientHint: RecipientHint;
  payoutClaimUrl?: string | null;
  operationId?: string | null;
  operation?: OriginationOperationDetail | null;
  createdAt: string;
  payoutBoundAt?: string;
  executableAt?: string;
  expiresAt?: string;
}

interface ListApiResponse {
  success: boolean;
  data: OriginationSummary[];
}

interface DetailApiResponse {
  success: boolean;
  data: OriginationDetail;
}

/** Lists send-side deferred originations for the claimed link. */
export const listOriginations = async (
  linkId: string
): Promise<OriginationSummary[]> => {
  const { data } = await widgetApi.get<ListApiResponse>(
    `/widget/links/${linkId}/deferred-payouts`
  );
  return data.data;
};

/** Fetches origination detail for the sender receipt screen. */
export const getOriginationDetail = async (
  linkId: string,
  originationId: string
): Promise<OriginationDetail> => {
  const { data } = await widgetApi.get<DetailApiResponse>(
    `/widget/links/${linkId}/deferred-payouts/${originationId}`
  );
  return data.data;
};
